/**
 * THE CITY GRID — where a city region's blocks and roads are, and the ONLY place that is decided.
 *
 * M3b draws the roads from this; M3c enumerates its block nodes from the same functions. That is the
 * whole reason this is a module and not arithmetic inlined in the column loop: if the road field and
 * the block enumeration each derive "where the grid is" for themselves, they will agree for the seed
 * you looked at and disagree somewhere else — buildings standing in the road, roads running through
 * lobbies. One grid, two readers.
 *
 * The grid is anchored on the region's FEATURE POINT (`RegionSample.centreX/centreZ`) and oriented by
 * a hash of the cell's id, so every city is laid out on its own axes and neighbouring cities meet at
 * an angle. It is a pure function of (cell identity, config): no noise, no state, nothing to memoise.
 *
 * THE COARSE RULE IS MAX-DOWNSAMPLE, AS IT IS FOR THE WALL — and it is worth saying why, because the
 * obvious alternative is wrong in a way that only shows up at range.
 *
 * A road is a line, and the reason the boundary wall max-downsamples is that ANY threshold on how
 * much of a coarse cell a line covers will DASH it. The cells walk along the line on their own
 * lattice while the line runs at its own angle, so the overlap oscillates, and wherever it dips under
 * the threshold the road has a gap. (Measured on the union coverage at lod 6: crossings clear 0.5 and
 * the roads between them do not — a grid of dots with no streets.) No threshold value fixes that;
 * it is the same "the information is not in the sample" result as the wall's stencil.
 *
 * So: a coarse cell is road if ANY of it is road. What that costs, and what the wall does not have to
 * pay, is WIDTH — a max-downsampled 6 m road on an 8 m lattice is drawn ~14 m wide. That is fine
 * while the block interior survives it and catastrophic when it does not: keep widening and the city
 * becomes a car park. Hence `roadDrawable`, which switches the grid off at the level where the road
 * would grow past half the block pitch. Roads then stay CONTINUOUS at every level they are drawn at,
 * and stop cleanly rather than degrading — which is the right trade, because what a city is meant to
 * read as from 4 km is its buildings, not its tarmac.
 */
import { hashInt2 } from '../../util/rng.js';
const ANGLE_SALT = 0xc17a5a1;
const PHASE_U_SALT = 0xc17b0b1;
const PHASE_V_SALT = 0xc17c0c1;
const unit = (h) => (h >>> 0) / 0x100000000;
/**
 * The grid for one city cell. A QUARTER TURN of rotation, not a full one: the lattice has 90 degree
 * symmetry, so a larger range would just alias onto itself and waste half the hash.
 */
export function cityGrid(cellId, spacing) {
    const a = unit(hashInt2(cellId, ANGLE_SALT)) * (Math.PI * 0.5);
    const cos = Math.cos(a), sin = Math.sin(a);
    return {
        cos,
        sin,
        phaseU: unit(hashInt2(cellId, PHASE_U_SALT)) * spacing,
        phaseV: unit(hashInt2(cellId, PHASE_V_SALT)) * spacing,
        spacing,
        reach: Math.abs(cos) + Math.abs(sin),
    };
}
/** World position in the grid's own frame, in metres. Grid lines lie on multiples of `spacing`. */
export function gridU(worldX, worldZ, centreX, centreZ, g) {
    const dx = worldX - centreX, dz = worldZ - centreZ;
    return dx * g.cos + dz * g.sin + g.phaseU;
}
export function gridV(worldX, worldZ, centreX, centreZ, g) {
    const dx = worldX - centreX, dz = worldZ - centreZ;
    return -dx * g.sin + dz * g.cos + g.phaseV;
}
/** Which block a position falls in, along one axis. The pair (u, v) indices name a block — M3c's key. */
export function blockIndex(t, spacing) {
    return Math.floor(t / spacing);
}
/** Signed offset from the nearest grid line along one axis. */
export function lineOffset(t, spacing) {
    return t - Math.round(t / spacing) * spacing;
}
/**
 * Distance from the interval `[t - h, t + h]` to the nearest grid line, along one axis. Zero when the
 * interval straddles a line — which is the max-downsample, stated as a distance so the caller can
 * compare it against the road's half-width exactly as the wall compares `dist`.
 */
export function axisReach(t, h, spacing) {
    return Math.max(0, Math.abs(lineOffset(t, spacing)) - Math.max(0, h));
}
/** Is any part of a WORLD-axis cell of half-extent `h` centred at (u, v) on a road? */
export function roadTouches(u, v, h, g, halfWidth) {
    const hg = h * g.reach;
    return axisReach(u, hg, g.spacing) <= halfWidth || axisReach(v, hg, g.spacing) <= halfWidth;
}
/**
 * May the grid be drawn at all on a lattice of half-extent `h`?
 *
 * The max-downsample draws the road up to `width + 2*sqrt(2)*h` wide (the worst-case projection of a
 * turned cell). Once that reaches half the block pitch the picture stops being a grid of streets and
 * becomes a paved field with holes in it, so the grid ends here rather than degrading through it.
 *
 * The WORST case rather than the actual angle, deliberately: whether the grid is drawable at a level
 * is then a property of the config alone, so two neighbouring cities cannot stop at different rings.
 * At the defaults (40 m blocks, 6 m roads) this holds to lod 5 and stops at lod 6, ~2 km out.
 */
export function roadDrawable(h, spacing, width) {
    return width + 2 * Math.SQRT2 * Math.max(0, h) <= spacing * 0.5;
}
// ============== Blocks, lots and buildings ==============
/**
 * Coarsest LOD at which individual buildings are ENUMERATED. Above it a city is drawn per COLUMN from
 * the block silhouette below — the same trade `MAX_SCATTER_LOD` makes for trees, and for the same
 * reason: the walk's cost goes with the area a coarse chunk covers, while what you can see of one
 * building goes the other way.
 *
 * It matches MAX_SCATTER_LOD today. It is a separate constant because the two are separate decisions:
 * a tree is 12 voxels and a tower is 800, so the level where "ask which one put this here" stops being
 * worth paying for need not be the same.
 */
export const MAX_BUILDING_LOD = 5;
const HEIGHT_SALT = 0xb1e1947;
const BLOCK_SALT = 0xb10c5;
const TIER_SALT = 0x71e45;
const MATERIAL_SALT = 0x9a7e21;
/** Stable identity of one block, from its city and its indices. */
export function blockSeed(cityId, bi, bj) {
    return hashInt2(hashInt2(cityId, BLOCK_SALT), hashInt2(bi, bj));
}
/** Inverse of gridU/gridV: back to world metres. */
export function worldFromGrid(u, v, centreX, centreZ, g) {
    const a = u - g.phaseU, b = v - g.phaseV;
    return { x: centreX + a * g.cos - b * g.sin, z: centreZ + a * g.sin + b * g.cos };
}
/**
 * How tall the city is here, as a fraction — 1 at the feature point, falling to `1 - falloff` at the
 * region's nominal edge. This is the whole difference between a skyline and a field of boxes, and it
 * costs one distance.
 */
export function downtownFactor(distFromCentre, radius, falloff) {
    const t = Math.min(1, Math.max(0, distFromCentre / Math.max(1, radius)));
    return 1 - falloff * t;
}
/**
 * THE BLOCK'S SILHOUETTE, in fine voxels above its seat — defined BEFORE any building and read by
 * both of them.
 *
 * Above MAX_BUILDING_LOD the coarse column draws this directly, and below it the block's building is
 * built up to exactly this height. So flying in across the cutoff changes the FOOTPRINT (a filled
 * block becomes a tiered tower) and never the height. Deriving the coarse stand-in from noise instead
 * would give a skyline that rises and falls as you approach, which is the defect the trees' shared
 * `columnDot` height exists to avoid.
 */
export function blockProfileHeight(seed, minH, maxH, bias, downtown, storeyHFine = 0) {
    const u = unit(hashInt2(seed, HEIGHT_SALT));
    // Biased so tall is RARE: at bias 3 a quarter of blocks pass 0.63 of the range and 1 in 30 passes 0.9.
    const t = Math.pow(u, Math.max(1, bias));
    const h = minH + (maxH - minH) * t * Math.max(0, downtown);
    // A WHOLE NUMBER OF STOREYS, decided HERE so the coarse mass and the real tower cannot disagree.
    // The roof is the top storey's ceiling and the top flight climbs to it, which is only exact if the
    // height is a multiple of the storey. Rounding it in `buildingNode` instead would give the mass a
    // different silhouette and the skyline would step as you crossed MAX_BUILDING_LOD.
    if (!(storeyHFine > 0))
        return h;
    return Math.max(1, Math.round(h / storeyHFine)) * storeyHFine;
}
/** The buildable rectangle of a block, in grid metres: the block square inset by the road and setback. */
export function blockLot(bi, bj, spacing, roadWidth, inset) {
    const pad = roadWidth * 0.5 + inset;
    const u0 = bi * spacing + pad, u1 = (bi + 1) * spacing - pad;
    const v0 = bj * spacing + pad, v1 = (bj + 1) * spacing - pad;
    return u1 > u0 && v1 > v0 ? { u0, u1, v0, v1 } : null;
}
/** Up to this many setbacks. Three reads as a tower; more is invisible at any distance you see it from. */
const MAX_TIERS = 3;
/**
 * A tiered tower from its seed. Each tier is shorter and narrower than the one below, which is what
 * makes a box read as a building — a plain extrusion at these sizes looks like a crate whatever its
 * proportions.
 *
 * Returned bottom-up, with `top` cumulative, so `emit` finds its tier with one scan and the AABB is
 * just tier 0's half-extents (every tier above is strictly inside it).
 */
export function buildingTiers(seed, halfU, halfV, height, storeyHFine = 0, minFirstTopFine = 0) {
    const n = 1 + Math.floor(unit(hashInt2(seed, TIER_SALT)) * MAX_TIERS);
    if (n <= 1)
        return [{ halfU, halfV, top: height }];
    const out = [];
    let hu = halfU, hv = halfV, base = 0;
    for (let i = 0; i < n; i++) {
        // Each tier takes a shrinking share of what is left, so the base is the tallest slab.
        const share = i === n - 1 ? 1 : 0.55;
        let top = i === n - 1 ? height : base + (height - base) * share;
        if (i < n - 1 && storeyHFine > 0) {
            // A SETBACK LANDS ON A FLOOR LEVEL OR IT DOES NOT HAPPEN.
            //
            // A terrace halfway up a storey is a ledge you cannot step onto and a storey that straddles two
            // footprints — its room walls then stand out over the balcony below, because the plate is the
            // tier at the slab and the shell above it is the narrower one. Snapping the top to a storey
            // makes the terrace exactly the floor level of the storey above, so you walk straight out.
            top = Math.round(top / storeyHFine) * storeyHFine;
            const lo = Math.max(base + storeyHFine, minFirstTopFine);
            const hi = height - storeyHFine;
            if (top < lo)
                top = lo;
            if (top > hi)
                top = hi;
            // Too short to hold another setback: this tier runs to the top and the tower stops here. The
            // `hi < lo` case is the one that bites — clamping down after clamping up would otherwise put a
            // setback at storey 1, i.e. inside the lobby.
            if (hi < lo || !(top > base) || !(top < height)) {
                out.push({ halfU: hu, halfV: hv, top: height });
                return out;
            }
        }
        out.push({ halfU: hu, halfV: hv, top });
        base = top;
        const k = 0.72 + 0.13 * unit(hashInt2(seed, TIER_SALT + i + 1));
        hu *= k;
        hv *= k;
    }
    return out;
}
/** Body materials a building may be built from — picked per building, so a block is not one colour. */
export function buildingMaterialIndex(seed, count) {
    return Math.floor(unit(hashInt2(seed, MATERIAL_SALT)) * count) % count;
}
//# sourceMappingURL=CityLayout.js.map